import { Component, Input, ElementRef, ViewChild, HostListener, ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'app-info-popover',
  templateUrl: './info-popover.component.html',
  styleUrls: ['./info-popover.component.scss']
})
export class InfoPopoverComponent {
  @Input() text = '';

  isOpen = false;
  popoverStyles: Record<string, any> = { opacity: '0', visibility: 'hidden' };
  arrowStyles: Record<string, any> = {};

  @ViewChild('wrapper', { static: true }) wrapperRef!: ElementRef;
  @ViewChild('popoverContent') popoverRef?: ElementRef;
  @ViewChild('button', { static: true }) buttonRef!: ElementRef;

  constructor(private cdr: ChangeDetectorRef) {}

  openPopover(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    setTimeout(() => this.updatePosition(), 0);
  }

  closePopover(): void {
    this.isOpen = false;
    this.popoverStyles = { opacity: '0', visibility: 'hidden' };
    this.arrowStyles = {};
  }

  togglePopover(): void {
    if (this.isOpen) {
      this.closePopover();
    } else {
      this.openPopover();
    }
  }

  @HostListener('window:resize') onWindowResize(): void {
    if (this.isOpen) {
      this.updatePosition();
    }
  }

  @HostListener('window:scroll', ['$event', '$event.target']) onWindowScroll(): void {
    if (this.isOpen) {
      this.updatePosition();
    }
  }

  private updatePosition(): void {
    const wrapperEl = this.wrapperRef?.nativeElement as HTMLElement | undefined;
    const popEl = this.popoverRef?.nativeElement as HTMLElement | undefined;
    const btnEl = this.buttonRef?.nativeElement as HTMLElement | undefined;
    if (!wrapperEl || !popEl || !btnEl) {
      if (this.isOpen) {
        setTimeout(() => this.updatePosition(), 0);
      }
      return;
    }

    const viewportPadding = 8;
    const arrowOffset = 8;
    const maxPopoverWidth = 520;
    const maxPopoverHeight = 220;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const maxWidth = Math.min(viewportWidth - viewportPadding * 2, maxPopoverWidth);
    popEl.style.maxWidth = `${maxWidth}px`;
    popEl.style.maxHeight = `${maxPopoverHeight}px`;

    void popEl.offsetHeight;

    const wrapperRect = wrapperEl.getBoundingClientRect();
    const btnRect = btnEl.getBoundingClientRect();
    const popRect = popEl.getBoundingClientRect();

    if (this.isOpen && (popRect.width === 0 || popRect.height === 0)) {
      setTimeout(() => this.updatePosition(), 0);
      return;
    }

    const btnCenterX = btnRect.left + btnRect.width / 2;
    let popLeftViewport = btnCenterX - popRect.width / 2;

    const minLeft = viewportPadding;
    const maxLeft = viewportWidth - popRect.width - viewportPadding;
    popLeftViewport = Math.max(minLeft, Math.min(maxLeft, popLeftViewport));

    const popLeftRelative = popLeftViewport - wrapperRect.left;

    let placingAbove = false;
    let popTopRelative: number;

    const spaceBelow = viewportHeight - btnRect.bottom;
    const spaceAbove = btnRect.top;
    const neededSpace = popRect.height + arrowOffset + viewportPadding;

    if (spaceBelow >= neededSpace || spaceBelow >= spaceAbove) {
      placingAbove = false;
      popTopRelative = btnRect.bottom - wrapperRect.top + arrowOffset;
    } else {
      placingAbove = true;
      popTopRelative = btnRect.top - wrapperRect.top - popRect.height - arrowOffset;
    }

    const arrowPadding = 12;
    const arrowLeftInPopover = btnCenterX - popLeftViewport;
    const arrowLeftClamped = Math.max(arrowPadding, Math.min(popRect.width - arrowPadding, arrowLeftInPopover));

    this.popoverStyles = {
      position: 'absolute',
      top: `${popTopRelative}px`,
      left: `${popLeftRelative}px`,
      maxWidth: `${maxWidth}px`,
      maxHeight: `${maxPopoverHeight}px`,
      overflowY: 'auto',
      opacity: '1',
      visibility: 'visible'
    };

    this.arrowStyles = {
      left: `${arrowLeftClamped}px`,
      transform: 'translateX(-50%) rotate(45deg)'
    };

    if (placingAbove) {
      this.arrowStyles['bottom'] = '-6px';
      this.arrowStyles['top'] = 'auto';
    } else {
      this.arrowStyles['top'] = '-6px';
      this.arrowStyles['bottom'] = 'auto';
    }

    this.cdr.detectChanges();
  }
}
